import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {Lookup, UtilsService} from "framework/services/utils.service";

@Injectable()
export class PolicyListService extends BaseListService<Models.PolicyV2ListRequest, Models.PolicyBatchRequest, Models.Policy>
{
    private readonly m_policiesByTenant: Lookup<Models.Policy[]>          = {};
    private readonly m_fetchingByTenant: Lookup<Promise<Models.Policy[]>> = {};

    private readonly m_policyByName: Lookup<Promise<Models.Policy | null>> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.Policy);
    }

    @ReportError
    public list(listReq: Models.PolicyV2ListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.policy.v2ListPolicies(listReq);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.PolicyBatchRequest): Promise<BatchResponse<Models.Policy>>
    {
        return this.domain.apis.policy.v2BatchPolicies(req);
    }

    protected createBatchRequest(listReq: Models.PolicyV2ListRequest,
                                 ids: string[]): Models.PolicyBatchRequest
    {
        return Models.PolicyBatchRequest.newInstance({ids});
    }

    public async getAllForTenant(tenantId: string): Promise<Models.Policy[]>
    {
        if (this.m_policiesByTenant[tenantId])
        {
            return this.m_policiesByTenant[tenantId];
        }

        if (!this.m_fetchingByTenant[tenantId])
        {
            this.m_fetchingByTenant[tenantId] = (async () =>
            {
                try
                {
                    const request = Models.PolicyV2ListRequest.newInstance({
                                                                               list_context: Models.ListContext.newInstance({batch_size: 500}),
                                                                               filters     : Models.PolicyFilter.newInstance({tenant_id: tenantId})
                                                                           });
                    const ids     = await this.listAll(request);
                    if (ids.length)
                    {
                        this.m_policiesByTenant[tenantId] = await this.batchAll(ids);
                    }
                }
                finally
                {
                    delete this.m_fetchingByTenant[tenantId];
                }

                return this.m_policiesByTenant[tenantId];
            })();
        }

        return this.m_fetchingByTenant[tenantId];
    }

    public resetTenantCache(tenantId: string): void
    {
        this.m_policiesByTenant[tenantId] = null;
    }

    public async findByName(tenantId: string,
                            name: string): Promise<Models.Policy | null>
    {
        const normalizedName = UtilsService.replaceUnderscoresWithSpaces(name)
                                           .toLowerCase();
        const cacheKey       = `${tenantId}::${normalizedName}`;

        if (!this.m_policyByName[cacheKey])
        {
            this.m_policyByName[cacheKey] = this.searchByName(tenantId, name, normalizedName)
                                                .catch(e =>
                                                       {
                                                           delete this.m_policyByName[cacheKey];
                                                           throw e;
                                                       });
        }

        return this.m_policyByName[cacheKey];
    }

    public async getBatchByIds(ids: string[]): Promise<Models.Policy[]>
    {
        return this.batchAll(ids, 1000);
    }

    private async searchByName(tenantId: string,
                               name: string,
                               normalizedName: string): Promise<Models.Policy | null>
    {
        const request  = Models.PolicyV2ListRequest.newInstance({
                                                                    list_context: Models.ListContext.newInstance({batch_size: 10}),
                                                                    filters     : Models.PolicyFilter.newInstance({
                                                                                                                      tenant_id: tenantId,
                                                                                                                      search   : name
                                                                                                                  })
                                                                });
        const response = await this.domain.apis.policy.v2ListPolicies(request);
        const ids      = response?.results ?? [];
        if (!ids.length) return null;

        const policies = await this.getBatchByIds(ids);
        return policies.find(policy => policy.name.toLowerCase() === normalizedName) ?? policies[0] ?? null;
    }
}
